const targets = new Map()
export function registerGuideTarget(key, definition) {
  const entry = { ...definition, key, token: Symbol(key) }
  if (!targets.has(key)) targets.set(key, [])
  targets.get(key).push(entry)
  return () => {
    const remaining = (targets.get(key) || []).filter(item => item.token !== entry.token)
    if (remaining.length) targets.set(key, remaining)
    else targets.delete(key)
  }
}
export function visibleGuideElement(element) {
  return !!(element && element.isConnected && Array.from(element.getClientRects()).some(rect => rect.width > 0 && rect.height > 0) && getComputedStyle(element).visibility !== 'hidden')
}
export function resolveGuideTarget(key, context = {}) {
  let candidates = (targets.get(key) || []).map(entry => ({ ...entry, element: typeof entry.element === 'function' ? entry.element() : entry.element }))
    .filter(entry => visibleGuideElement(entry.element) && (!entry.ready || entry.ready()))
  if (context.modalKey) candidates = candidates.filter(entry => entry.context && entry.context.modalKey === context.modalKey)
  if (context.pageId) candidates = candidates.filter(entry => !entry.context || !entry.context.pageId || String(entry.context.pageId) === String(context.pageId))
  if (context.moduleInstance) candidates = candidates.filter(entry => entry.context && String(entry.context.moduleInstance) === String(context.moduleInstance))
  const dialogs = candidates.filter(entry => entry.element.closest('.v-dialog--active'))
  if (dialogs.length) candidates = dialogs
  if (context.occurrence && targets.has(key)) {
    candidates.sort((a, b) => a.element === b.element ? 0 : a.element.compareDocumentPosition(b.element) & 4 ? -1 : 1)
    return candidates[Number(context.occurrence) - 1] || null
  }
  if (candidates.length === 1) return candidates[0]
  if (candidates.length > 1) return null // Require an explicit context for ambiguous instances.
  if (targets.has(key)) return null
  const elements = Array.from(document.querySelectorAll('[data-guide-target]')).filter(element => element.getAttribute('data-guide-target') === key && visibleGuideElement(element))
  const inDialogs = elements.filter(element => element.closest('.v-dialog--active'))
  const available = inDialogs.length ? inDialogs : elements
  if (context.occurrence) return available[Number(context.occurrence) - 1] ? { key, element: available[Number(context.occurrence) - 1], operations: {} } : null
  return available.length === 1 ? { key, element: available[0], operations: {} } : null
}
export function waitForGuideTarget(key, { timeout = 15000, signal, context = {} } = {}) {
  return new Promise((resolve, reject) => {
    let timer, poll, observer
    const cleanup = () => { clearTimeout(timer); clearInterval(poll); if (observer) observer.disconnect(); if (signal) signal.removeEventListener('abort', aborted) }
    const aborted = () => { cleanup(); reject(new Error('cancelled')) }
    const check = () => { const target = resolveGuideTarget(key, context); if (target) { cleanup(); resolve(target) } }
    if (signal && signal.aborted) return aborted()
    if (signal) signal.addEventListener('abort', aborted, { once: true })
    timer = setTimeout(() => { cleanup(); reject(new Error('Cette cible est indisponible, masquée ou ambiguë.')) }, timeout)
    observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true })
    poll = setInterval(check, 150)
    check()
  })
}
export function guideTargetCatalog() {
  return Array.from(targets.entries()).flatMap(([key, entries]) => entries.map(entry => ({ key, label: entry.label || key, context: entry.context, operations: Object.keys(entry.operations || {}) })))
}
export function selectableGuideTargets() {
  const registered = Array.from(targets.values()).flatMap(entries => entries.map(entry => ({ ...entry, element: typeof entry.element === 'function' ? entry.element() : entry.element })))
  const declared = Array.from(document.querySelectorAll('[data-guide-target]')).filter(element => {
    if (targets.has(element.getAttribute('data-guide-target'))) return false
    // Explicit DOM-only anchors (template fields and save buttons) are supported,
    // but cannot bypass an enclosing module's readiness contract.
    return !registered.some(entry => entry.element && entry.element !== element && entry.element.contains(element) && entry.ready && !entry.ready())
  }).map(element => ({ key: element.getAttribute('data-guide-target'), element, operations: {} }))
  return registered.concat(declared)
    .filter(entry => visibleGuideElement(entry.element) && (!entry.ready || entry.ready()))
}
export function describeGuideElement(element) {
  const key = element.getAttribute('data-guide-target')
  const registered = (targets.get(key) || []).find(entry => (typeof entry.element === 'function' ? entry.element() : entry.element) === element)
  const context = registered && registered.context && registered.context.pageId ? { pageId: Number(registered.context.pageId) } : {}
  // Vue instance IDs and modal keys are ephemeral; persist a DOM occurrence instead.
  const peers = selectableGuideTargets().filter(entry => entry.key === key && (!context.pageId || !entry.context || !entry.context.pageId || Number(entry.context.pageId) === context.pageId)).map(entry => entry.element).filter(item => !!item.closest('.v-dialog--active') === !!element.closest('.v-dialog--active'))
  peers.sort((a, b) => a === b ? 0 : a.compareDocumentPosition(b) & 4 ? -1 : 1)
  if (peers.length > 1) context.occurrence = peers.indexOf(element) + 1
  return { key, label: registered && registered.label || element.getAttribute('aria-label') || element.textContent.trim().slice(0, 100), context }
}
